import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    IconLoader,
    IconArrowLeft,
} from "@tabler/icons-react";
import SharedPrihlaskaForm from "../../components/SharedPrihlaskaForm";

interface Person {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    address: string;
    houseNumber?: string;
    city?: string;
    psc?: string;
    rodCislo?: string;
    isBernolakova: boolean;
    schoolName?: string;
    gender?: 'MALE' | 'FEMALE';
    type: 'PARENT' | 'ADULT' | 'KID';
    schoolyearId?: string;
    schoolyear?: {
        id: string;
        name: string;
        order: number;
        type: 'PRESCHOOL' | 'PRIMARY' | 'HIGHSCHOOL' | 'UNIVERSITY';
    };
}

const CreatePrihlaska = () => {
    const { personId } = useParams<{ personId: string }>();
    const navigate = useNavigate();
    const [person, setPerson] = useState<Person | null>(null);
    const [existingPrihlaska, setExistingPrihlaska] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Ensure personId is provided
                if (!personId) {
                    navigate('/main');
                    return;
                }

                // Fetch person data
                const personRes = await fetch(`${process.env.REACT_APP_PATH}/api/user/data/profile`, {
                    credentials: 'include',
                });

                if (!personRes.ok) {
                    throw new Error('Failed to fetch profile');
                }

                const userData = await personRes.json();
                const foundPerson = userData.data.person.find((p: Person) => p.id === personId);

                if (!foundPerson) {
                    navigate('/main');
                    return;
                }

                // Don't allow prihlaska creation for parents
                if (foundPerson.type === 'PARENT') {
                    navigate('/main');
                    return;
                }

                setPerson(foundPerson);

                // Check if person already has a prihlaska
                const prihlasky = userData.data.prihlaska || [];
                const foundPrihlaska = prihlasky.find((p: any) => p.personId === personId);

                if (foundPrihlaska) {
                    setExistingPrihlaska(foundPrihlaska);
                }

                setLoading(false);
            } catch (err) {
                console.error('Failed to fetch data:', err);
                setError('Nepodarilo sa načítať údaje');
                setLoading(false);
            }
        };

        fetchData();
    }, [personId, navigate]);



    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
                <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                    <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full">
                        <div className="text-center">
                            <IconLoader className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
                            <h2 className="text-xl font-semibold text-gray-900">Načítavam údaje...</h2>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!person) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
                <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                    <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full">
                        <div className="text-center">
                            <h2 className="text-xl font-semibold text-gray-900">Osoba sa nenašla</h2>
                            <button
                                onClick={() => navigate('/main')}
                                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Späť na profil
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
                <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                    <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full">
                        <div className="text-center">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Chyba</h2>
                            <p className="text-gray-600 mb-4">{error}</p>
                            <button
                                onClick={() => navigate('/main')}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Späť na profil
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            {/* Header */}
            <header className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center">
                            <img src="/img/3h.png" alt="Logo" className="w-10 h-10 mr-3" />
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">SCVČ Tri Hôrky</h1>
                                <p className="text-xs text-gray-500">Údaje osoby a nová prihláška</p>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate('/main')}
                            className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900 font-medium bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1 transition-colors"
                        >
                            <IconArrowLeft className="h-4 w-4" />
                            <span>Späť</span>
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <SharedPrihlaskaForm
                    person={person}
                    existingPrihlaska={existingPrihlaska}
                    isAdminMode={false}
                    onSuccess={undefined}
                    onCancel={() => navigate('/main')}
                />
            </div>
        </div>
    );
};

export default CreatePrihlaska;
